import { cn } from "@/utils";
import { Check, Copy, Terminal } from "lucide-react";
import { useState } from "react";

/**
 * Terminal-style rendering of a shell command (`$ ...`). Shared between the
 * executed e2b_bash renderer (Tool_E2bBash) and the pending tool-call
 * confirmation, so the command looks identical before and after it runs.
 *
 * Theme-aware: a light surface with dark text in light mode, and a near-black
 * surface with light text in dark mode (rather than always-dark, which read as
 * too bright in dark mode).
 */
export function CommandBlock({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);
  const isLong = command.split("\n").length > 6;
  const [expanded, setExpanded] = useState(!isLong);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group/cmd bg-neutral-100 dark:bg-neutral-950">
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-neutral-200 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 text-xs">
        <Terminal size={12} />
        <span>Command</span>
      </div>
      <div className="relative">
        <div
          className={cn(
            "px-3 py-2.5 overflow-x-auto",
            !expanded && "max-h-[120px] overflow-hidden",
          )}
        >
          <pre className="text-sm font-mono text-neutral-800 dark:text-neutral-100 whitespace-pre-wrap break-all leading-relaxed">
            <span className="text-emerald-600 dark:text-emerald-400 select-none">
              ${" "}
            </span>
            {command}
          </pre>
        </div>
        {isLong && !expanded && (
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-neutral-100 dark:from-neutral-950 to-transparent" />
        )}
      </div>
      {isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full py-1.5 text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors border-t border-neutral-200 dark:border-neutral-800"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
      <button
        onClick={handleCopy}
        className={cn(
          "absolute top-9 right-2 p-1.5 rounded-md transition-all",
          "opacity-0 group-hover/cmd:opacity-100",
          "bg-neutral-200 hover:bg-neutral-300 text-neutral-600",
          "dark:bg-neutral-800 dark:hover:bg-neutral-700 dark:text-neutral-300",
        )}
        aria-label="Copy command"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}
